/*eslint max-lines-per-function: ["error", 500]*/

import type { TFunction } from 'i18next';

import { GridRecord } from '../../components/Grid/GridDataTypes';
import {
  CreateUpdatePurchaseInvoiceDto,
  CreateUpdatePurchaseInvoiceDtoFromJSON,
  CreateUpdatePurchaseInvoiceEstimateItemsDto,
  CreateUpdatePurchaseInvoiceTeamsAndProjectsDto,
  CreateUpdatePurchaseInvoiceTeamsAndProjectsDtoFromJSON,
  DefaultApi,
  GetLastEditorDto,
  GetPurchaseInvoiceDto,
  GetPurchaseInvoiceEstimateItemDto,
  GetPurchaseInvoiceExtendedDto,
  GetPurchaseInvoiceForItem,
  GetPurchaseInvoiceWithAttachmentIdDto,
  ImportGeneralRowCreateDto,
  ImportGoogleRowCreateDto,
  PrepareGeneralImportDto,
  PrepareGoogleImportDto,
  PreparedGeneralRowDto,
  PreparedGoogleRowDto,
} from '../autogenerated/pokApiClient';
import {
  PurchaseInvoiceEstimateItemsTabSchema,
  PurchaseInvoiceGeneralTabSchema,
  PurchaseInvoiceTeamsProjectsTabSchema,
} from '../validation/schemas';
import { validateAgainst } from '../validation/validateAgainst';
import {
  getSummary,
  purchaseInvoiceForGrid,
} from '../../../pok/components/PurchaseInvoices/PurchaseInvoiceSearch';
import { PokCoreContextType } from '../pokCore';
import { NotificationsProviderType } from '../../notifications';
import * as AttachmentContext from '../../../common/pokCore/contexts/AttachmentContext';
import { getMainCurrencySymbol } from '../../../utils/currencyFormatUtils';
import { AttachmentObjectType, InvoiceTypeEnum } from '../validation/enums';
import { TKeys } from '../../hooks/useTranslation';

export interface PurchaseInvoiceContextInterface {
  getById: (id: string) => Promise<GetPurchaseInvoiceExtendedDto>;
  approve: (id: string) => Promise<void>;
  unApprove: (id: string) => Promise<void>;
  deactivate: (id: string, reason: string) => Promise<void>;
  getAllByPortion: (
    t: TFunction<'fvzLng', 'fvzLng'>,
    tk: TKeys<'fvzLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllWithTeamsByPortion: (
    t: TFunction<'fvzLng', 'fvzLng'>,
    tk: TKeys<'fvzLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllUnsettledWithProjectsByPortion: (
    t: TFunction<'fvzLng', 'fvzLng'>,
    tk: TKeys<'fvzLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    projectId?: string,
  ) => Promise<GridRecord[]>;
  getAllUnsettledWithProjectsCount: (
    filterText?: string,
    filterColumns?: string,
    projectId?: string,
  ) => Promise<number>;
  getAllForMediaplanerByPortion: (
    t: TFunction<'fvzLng', 'fvzLng'>,
    tk: TKeys<'fvzLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllForMediaplanerCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getAllTvForMediaplanerByPortion: (
    t: TFunction<'fvzLng', 'fvzLng'>,
    tk: TKeys<'fvzLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllTvForMediaplanerCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getAllUnsettledTvByPortion: (
    t: TFunction<'fvzLng', 'fvzLng'>,
    tk: TKeys<'fvzLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllUnsettledTvCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getEstimateItems: (
    id: string,
  ) => Promise<GetPurchaseInvoiceEstimateItemDto[]>;
  getByEstimateItem: (
    estimateItemId: string,
  ) => Promise<GetPurchaseInvoiceForItem[]>;
  getByProject: (
    projectId: string,
    month?: Date,
    profileId?: string,
  ) => Promise<GetPurchaseInvoiceWithAttachmentIdDto[]>;
  getAllCount: (filterText?: string, filterColumns?: string) => Promise<number>;
  getAllWithTeamsCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  update: (id: string, dto: CreateUpdatePurchaseInvoiceDto) => Promise<void>;
  updateTeamsAndProjects: (
    id: string,
    dto: CreateUpdatePurchaseInvoiceTeamsAndProjectsDto,
  ) => Promise<void>;
  updateEstimateItems: (
    id: string,
    dto: CreateUpdatePurchaseInvoiceEstimateItemsDto,
  ) => Promise<void>;
  create: (
    dto: CreateUpdatePurchaseInvoiceDto,
  ) => Promise<GetPurchaseInvoiceDto>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  getLastEstimateItemEditor: (id: string) => Promise<GetLastEditorDto>;
  getAllReadyToSentByPortion: (
    t: TFunction<'fvzLng', 'fvzLng'>,
    tk: TKeys<'fvzLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    type?: InvoiceTypeEnum,
    isRepeat?: boolean,
  ) => Promise<GridRecord[]>;
  getCountReadyToSent: (
    filterText?: string,
    filterColumns?: string,
    type?: InvoiceTypeEnum,
    isRepeat?: boolean,
  ) => Promise<number>;
  getAllGeneratedByPortion: (
    t: TFunction<'fvzLng', 'fvzLng'>,
    tk: TKeys<'fvzLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getCountGenerated: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getAllGeneratedSettledByPortion: (
    t: TFunction<'fvzLng', 'fvzLng'>,
    tk: TKeys<'fvzLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getCountGeneratedSettled: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getAllWithoutTeamsAndProjectsPortion: (
    t: TFunction<'fvzLng', 'fvzLng'>,
    tk: TKeys<'fvzLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getCountWithoutTeamsAndProjects: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getApprovedAutomaticallyPortion: (
    t: TFunction<'fvzLng', 'fvzLng'>,
    tk: TKeys<'fvzLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getCountApprovedAutomatically: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getAllByPackageFVZ: (
    t: TFunction<'fvzLng', 'fvzLng'>,
    tk: TKeys<'fvzLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    packageFVZId?: string,
  ) => Promise<GridRecord[]>;
  getCountByPackageFVZ: (
    filterText?: string,
    packageFVZId?: string,
  ) => Promise<number>;
  getZipByPurchaseIds: (
    projectNumber: string,
    purchaseInvoiceIds: string[],
  ) => Promise<object>;
  getSumAllWithTeamsForPurchaseInvoices: (
    t: TFunction<'fvzLng', 'fvzLng'>,
    tk: TKeys<'fvzLng'>,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord>;
  getSumAllUnsettledWithProjectsForPurchaseInvoices: (
    t: TFunction<'fvzLng', 'fvzLng'>,
    tk: TKeys<'fvzLng'>,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord>;
  getSumAllUnsettledTvForPurchaseInvoices: (
    t: TFunction<'fvzLng', 'fvzLng'>,
    tk: TKeys<'fvzLng'>,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord>;
  getByClientAndDateByPortion: (
    t: TFunction<'fvzLng', 'fvzLng'>,
    tk: TKeys<'fvzLng'>,
    pageSize: number,
    pageNumber: number,
    clientId?: string,
    issueDate?: Date,
    currentInvoiceId?: string,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getByClientAndDate: (
    clientId?: string,
    issueDate?: Date,
    currentInvoiceId?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  prepareGoogleImport: (
    dto: PrepareGoogleImportDto,
  ) => Promise<PreparedGoogleRowDto[]>;
  prepareGeneralImport: (
    dto: PrepareGeneralImportDto,
  ) => Promise<PreparedGeneralRowDto[]>;
  createGoogleImportRow: (dto: ImportGoogleRowCreateDto) => Promise<void>;
  createGeneralImportRow: (dto: ImportGeneralRowCreateDto) => Promise<void>;
}

export const PurchaseInvoiceContext = (
  api: DefaultApi,
): PurchaseInvoiceContextInterface => {
  return {
    getById: (id: string) => api.purchaseInvoiceControllerGet(id),
    approve: (id: string) => api.purchaseInvoiceControllerApprove(id),
    unApprove: (id: string) => api.purchaseInvoiceControllerUnApprove(id),
    deactivate: (id: string, reason: string) =>
      api.purchaseInvoiceControllerDeactivate(id, reason),
    getAllByPortion: async (
      t: TFunction<'fvzLng', 'fvzLng'>,
      tk: TKeys<'fvzLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.purchaseInvoiceControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );

      return data.map(item => purchaseInvoiceForGrid(t, tk, item, orderBy));
    },
    getAllWithTeamsByPortion: async (
      t: TFunction<'fvzLng', 'fvzLng'>,
      tk: TKeys<'fvzLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.purchaseInvoiceControllerGetAllWithTeamsByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );

      return data.map(item => purchaseInvoiceForGrid(t, tk, item, orderBy));
    },
    getAllUnsettledWithProjectsByPortion: async (
      t: TFunction<'fvzLng', 'fvzLng'>,
      tk: TKeys<'fvzLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      projectId?: string,
    ) => {
      const data =
        await api.purchaseInvoiceControllerGetAllUnsettledWithProjectsByPortion(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          filterColumns,
          projectId,
        );

      return data.map(item => purchaseInvoiceForGrid(t, tk, item, orderBy));
    },

    getAllUnsettledWithProjectsCount: (
      filterText?: string,
      filterColumns?: string,
      projectId?: string,
    ) =>
      api.purchaseInvoiceControllerGetAllUnsettledWithProjectsCount(
        filterText || '',
        filterColumns,
        projectId,
      ),

    getAllForMediaplanerByPortion: async (
      t: TFunction<'fvzLng', 'fvzLng'>,
      tk: TKeys<'fvzLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data =
        await api.purchaseInvoiceControllerGetAllForMediaplanerByPortion(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          filterColumns,
        );

      return data.map(item => purchaseInvoiceForGrid(t, tk, item, orderBy));
    },

    getAllForMediaplanerCount: (filterText?: string, filterColumns?: string) =>
      api.purchaseInvoiceControllerGetAllForMediaplanerCount(
        filterText || '',
        filterColumns,
      ),

    getAllTvForMediaplanerByPortion: async (
      t: TFunction<'fvzLng', 'fvzLng'>,
      tk: TKeys<'fvzLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data =
        await api.purchaseInvoiceControllerGetAllTvForMediaplanerByPortion(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          filterColumns,
        );

      return data.map(item => purchaseInvoiceForGrid(t, tk, item, orderBy));
    },

    getAllTvForMediaplanerCount: (
      filterText?: string,
      filterColumns?: string,
    ) =>
      api.purchaseInvoiceControllerGetAllTvForMediaplanerCount(
        filterText || '',
        filterColumns,
      ),
    getAllUnsettledTvByPortion: async (
      t: TFunction<'fvzLng', 'fvzLng'>,
      tk: TKeys<'fvzLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data =
        await api.purchaseInvoiceControllerGetAllUnsettledTvByPortion(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          filterColumns,
        );

      return data.map(item => purchaseInvoiceForGrid(t, tk, item, orderBy));
    },

    getAllUnsettledTvCount: (filterText?: string, filterColumns?: string) =>
      api.purchaseInvoiceControllerGetAllUnsettledTvCount(
        filterText || '',
        filterColumns,
      ),

    getAllCount: (filterText?: string, filterColumns?: string) =>
      api.purchaseInvoiceControllerGetAllCount(filterText || '', filterColumns),
    getAllWithTeamsCount: (filterText?: string, filterColumns?: string) =>
      api.purchaseInvoiceControllerGetAllWithTeamsCount(
        filterText || '',
        filterColumns,
      ),

    getEstimateItems: (id: string) =>
      api.purchaseInvoiceControllerGetEstimateItems(id),
    getByEstimateItem: (estimateItemId: string) =>
      api.purchaseInvoiceControllerFindByEstimateItem(estimateItemId),
    getByProject: (projectId: string, month?: Date, profileId?: string) =>
      api.purchaseInvoiceControllerFindByProject(projectId, month, profileId),
    update: (id: string, dto: CreateUpdatePurchaseInvoiceDto) =>
      api.purchaseInvoiceControllerUpdate(id, dto),
    updateTeamsAndProjects: (
      id: string,
      dto: CreateUpdatePurchaseInvoiceTeamsAndProjectsDto,
    ) => api.purchaseInvoiceControllerUpdateTeamsAndProjects(id, dto),
    updateEstimateItems: (
      id: string,
      dto: CreateUpdatePurchaseInvoiceEstimateItemsDto,
    ) =>
      api.purchaseInvoiceControllerUpdatePurchaseInvoiceEstimateItems(id, dto),
    create: (dto: CreateUpdatePurchaseInvoiceDto) =>
      api.purchaseInvoiceControllerCreate(dto),
    getLastEditor: (id: string) =>
      api.purchaseInvoiceControllerGetLastEditor(id),
    getLastEstimateItemEditor: (id: string) =>
      api.purchaseInvoiceControllerGetLastEstimateItemEditor(id),
    getAllReadyToSentByPortion: async (
      t: TFunction<'fvzLng', 'fvzLng'>,
      tk: TKeys<'fvzLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      type?: InvoiceTypeEnum,
      isRepeat?: boolean,
    ) => {
      const data =
        await api.purchaseInvoiceControllerGetAllReadyToSentByPortion(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          filterColumns,
          isRepeat || false,
          type,
        );

      return data.map(item => purchaseInvoiceForGrid(t, tk, item, orderBy));
    },

    getCountReadyToSent: (
      filterText?: string,
      filterColumns?: string,
      type?: InvoiceTypeEnum,
      isRepeat?: boolean,
    ) =>
      api.purchaseInvoiceControllerGetCountReadyToSent(
        filterText || '',
        filterColumns,
        type,
        isRepeat || false,
      ),

    getAllGeneratedByPortion: async (
      t: TFunction<'fvzLng', 'fvzLng'>,
      tk: TKeys<'fvzLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.purchaseInvoiceControllerGetAllGeneratedByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );

      return data.map(item => purchaseInvoiceForGrid(t, tk, item, orderBy));
    },

    getCountGenerated: (filterText?: string, filterColumns?: string) =>
      api.purchaseInvoiceControllerGetCountGenerated(
        filterText || '',
        filterColumns,
      ),
    getAllGeneratedSettledByPortion: async (
      t: TFunction<'fvzLng', 'fvzLng'>,
      tk: TKeys<'fvzLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data =
        await api.purchaseInvoiceControllerGetAllGeneratedSettledByPortion(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          filterColumns,
        );

      return data.map(item => purchaseInvoiceForGrid(t, tk, item, orderBy));
    },

    getCountGeneratedSettled: (filterText?: string, filterColumns?: string) =>
      api.purchaseInvoiceControllerGetCountGeneratedSettled(
        filterText || '',
        filterColumns,
      ),
    getAllWithoutTeamsAndProjectsPortion: async (
      t: TFunction<'fvzLng', 'fvzLng'>,
      tk: TKeys<'fvzLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data =
        await api.purchaseInvoiceControllerGetAllWithoutTeamsAndProjectsPortion(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          filterColumns,
        );

      return data.map(item => purchaseInvoiceForGrid(t, tk, item, orderBy));
    },

    getCountWithoutTeamsAndProjects: (
      filterText?: string,
      filterColumns?: string,
    ) =>
      api.purchaseInvoiceControllerGetCountWithoutTeamsAndProjects(
        filterText || '',
        filterColumns,
      ),
    getApprovedAutomaticallyPortion: async (
      t: TFunction<'fvzLng', 'fvzLng'>,
      tk: TKeys<'fvzLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data =
        await api.purchaseInvoiceControllerGetApprovedAutomaticallyPortion(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          filterColumns,
        );

      return data.map(item => purchaseInvoiceForGrid(t, tk, item, orderBy));
    },

    getCountApprovedAutomatically: (
      filterText?: string,
      filterColumns?: string,
    ) =>
      api.purchaseInvoiceControllerGetCountApprovedAutomatically(
        filterText || '',
        filterColumns,
      ),

    getAllByPackageFVZ: async (
      t: TFunction<'fvzLng', 'fvzLng'>,
      tk: TKeys<'fvzLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      packageFVZId?: string,
    ) => {
      const data =
        await api.purchaseInvoiceControllerGetAllByPackageFVZByPortion(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          packageFVZId || '',
        );

      return data.map(item => purchaseInvoiceForGrid(t, tk, item, orderBy));
    },

    getCountByPackageFVZ: (filterText?: string, packageFVZId?: string) =>
      api.purchaseInvoiceControllerGetCountByPackageFVZ(
        filterText || '',
        packageFVZId || '',
      ),
    getZipByPurchaseIds: (
      projectNumber: string,
      purchaseInvoiceIds: string[],
    ) =>
      api.purchaseInvoiceControllerGetZipByPurchaseIds(
        projectNumber,
        purchaseInvoiceIds,
      ),

    getSumAllWithTeamsForPurchaseInvoices: async (
      t: TFunction<'fvzLng', 'fvzLng'>,
      tk: TKeys<'fvzLng'>,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data =
        await api.purchaseInvoiceControllerGetSumAllWithTeamsForPurchaseInvoices(
          filterText || '',
          filterColumns,
        );
      return getSummary(t, tk, data);
    },
    getSumAllUnsettledWithProjectsForPurchaseInvoices: async (
      t: TFunction<'fvzLng', 'fvzLng'>,
      tk: TKeys<'fvzLng'>,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data =
        await api.purchaseInvoiceControllerGetSumAllUnsettledWithProjectsForPurchaseInvoices(
          filterText || '',
          filterColumns,
        );
      return getSummary(t, tk, data);
    },
    getSumAllUnsettledTvForPurchaseInvoices: async (
      t: TFunction<'fvzLng', 'fvzLng'>,
      tk: TKeys<'fvzLng'>,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data =
        await api.purchaseInvoiceControllerGetSumAllUnsettledTvForPurchaseInvoices(
          filterText || '',
          filterColumns,
        );
      return getSummary(t, tk, data);
    },
    getByClientAndDateByPortion: async (
      t: TFunction<'fvzLng', 'fvzLng'>,
      tk: TKeys<'fvzLng'>,
      pageSize: number,
      pageNumber: number,
      clientId?: string,
      issueDate?: Date,
      currentInvoiceId?: string,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data =
        await api.purchaseInvoiceControllerGetByClientAndDateByPortion(
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          filterColumns,
          clientId,
          issueDate,
          currentInvoiceId,
        );
      return data.map(item => purchaseInvoiceForGrid(t, tk, item, orderBy));
    },
    getByClientAndDate: (
      clientId?: string,
      issueDate?: Date,
      currentInvoiceId?: string,
      filterText?: string,
      filterColumns?: string,
    ) =>
      api.purchaseInvoiceControllerGetByClientAndDate(
        filterText || '',
        filterColumns,
        clientId,
        issueDate,
        currentInvoiceId,
      ),
    prepareGoogleImport: (dto: PrepareGoogleImportDto) =>
      api.purchaseInvoiceControllerPrepareGoogleImport(dto),
    prepareGeneralImport: (dto: PrepareGeneralImportDto) =>
      api.purchaseInvoiceControllerPrepareGeneralImport(dto),
    createGoogleImportRow: (dto: ImportGoogleRowCreateDto) =>
      api.purchaseInvoiceControllerCreateGoogleImportRow(dto),
    createGeneralImportRow: (dto: ImportGeneralRowCreateDto) =>
      api.purchaseInvoiceControllerCreateGeneralImportRow(dto),
  };
};

export const newPurchaseInvoice = (companyId?: string) =>
  CreateUpdatePurchaseInvoiceDtoFromJSON({
    type: InvoiceTypeEnum.Domestic,
    currency: getMainCurrencySymbol(),
    positions: [],
    companyId,
  });

export const newPurchaseInvoiceTeamsAndProjects = () =>
  CreateUpdatePurchaseInvoiceTeamsAndProjectsDtoFromJSON({
    teamIds: [],
    projectIds: [],
  });

export const newPurchaseInvoiceEstimateItems = () => [];

export const validateGeneralTab = (
  purchaseInvoice: CreateUpdatePurchaseInvoiceDto,
) => {
  if (
    purchaseInvoice.currency !== getMainCurrencySymbol() &&
    !purchaseInvoice.exchangeRate
  ) {
    return {
      valid: false,
      errors: [`Dla waluty ${purchaseInvoice.currency} proszę podać kurs.`],
    };
  }
  return validateAgainst(PurchaseInvoiceGeneralTabSchema, purchaseInvoice);
};

export const validateTeamsProjectsTab = (
  teamsAndProjects: CreateUpdatePurchaseInvoiceTeamsAndProjectsDto,
) => validateAgainst(PurchaseInvoiceTeamsProjectsTabSchema, teamsAndProjects);

export const validateEstimateItemsTab = (
  estimateItems: CreateUpdatePurchaseInvoiceEstimateItemsDto,
) => validateAgainst(PurchaseInvoiceEstimateItemsTabSchema, estimateItems);

export const convert = (
  companyId?: string,
  purchaseInvoice?: GetPurchaseInvoiceExtendedDto,
) => {
  if (!purchaseInvoice) {
    return newPurchaseInvoice(companyId);
  }
  const converted = CreateUpdatePurchaseInvoiceDtoFromJSON(purchaseInvoice);
  converted.clientId = purchaseInvoice.client?.id;
  converted.salesInvoiceId = purchaseInvoice.salesInvoice?.id;
  converted.originalInvoiceId = purchaseInvoice.originalInvoice?.id;

  if (!converted.comment) {
    converted.comment = '';
  }

  return converted;
};

export const convertTeamsAndProjects = (
  purchaseInvoice?: GetPurchaseInvoiceExtendedDto,
) => {
  if (!purchaseInvoice) {
    return newPurchaseInvoiceTeamsAndProjects();
  }
  const converted = CreateUpdatePurchaseInvoiceTeamsAndProjectsDtoFromJSON({
    teamIds: purchaseInvoice.teamIds,
    projectIds: purchaseInvoice.projectIds,
  });

  return converted;
};

export const convertPurchaseInvoiceEstimateItems = (
  purchaseInvoice?: GetPurchaseInvoiceExtendedDto,
) => {
  if (!purchaseInvoice) {
    return newPurchaseInvoiceEstimateItems();
  }
  const converted =
    purchaseInvoice.purchaseInvoiceEstimateItems?.map(a => {
      return {
        amount: a.amount,
        estimateItemId: a.estimateItemId,
        projectId: a.estimateItem.estimateByMonth.project.id,
      };
    }) || [];

  return converted;
};

export const createNewAttachment = (id?: string) => {
  const attachment = AttachmentContext.newAttachment();
  attachment.enObjectType = AttachmentObjectType.PurchaseInvoice;
  attachment.objectId = id as string;
  return attachment;
};

export const forPurchaseInvoiceView = async (
  pok: PokCoreContextType,
  notifications: NotificationsProviderType,
  id: string,
  action: () => void,
) => {
  const attachments = await pok.attachments.getAllForObject(
    AttachmentObjectType.PurchaseInvoice,
    id,
  );

  const ac = AttachmentContext.forAssociatedDocuments;

  const assocAttachments = ac(pok, notifications, attachments, action);

  assocAttachments.new = () => createNewAttachment(id as string);
  return {
    attachments: assocAttachments,
  };
};

export const notesForPurchaseInvoiceView = async (
  pok: PokCoreContextType,
  notifications: NotificationsProviderType,
  id: string,
  action: () => void,
) => {
  const attachments = await pok.attachments.getAllForObjectAndShortname(
    AttachmentObjectType.PurchaseInvoice,
    id,
    'NOTE',
  );

  const ac = AttachmentContext.forAssociatedDocuments;

  const assocAttachments = ac(pok, notifications, attachments, action);

  assocAttachments.new = () => {
    const attachment = AttachmentContext.newAttachment();
    attachment.enObjectType = AttachmentObjectType.PurchaseInvoice;
    attachment.objectId = id;
    return attachment;
  };
  return {
    attachments: assocAttachments,
  };
};
